import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { reviewCommentApi } from '../lib/api';
import {
  ChatBubbleLeftRightIcon,
  CheckCircleIcon,
  ArrowUturnLeftIcon,
  PaperAirplaneIcon,
} from '@heroicons/react/24/outline';
import { formatDistanceToNow } from 'date-fns';
import toast from 'react-hot-toast';

interface ReviewCommentsProps {
  reportId: string;
}

interface Comment {
  id: string;
  content: string;
  violationRef?: string | null;
  sectionRef?: string | null;
  resolved: boolean;
  resolvedAt?: string | null;
  resolvedBy?: string | null;
  createdAt: string;
  author: {
    id: string;
    email: string;
    firstName?: string | null;
    lastName?: string | null;
  };
  replies?: Comment[];
}

function CommentItem({
  comment,
  reportId,
  isReply = false,
  onReply,
}: {
  comment: Comment;
  reportId: string;
  isReply?: boolean;
  onReply?: (parentId: string) => void;
}) {
  const queryClient = useQueryClient();

  const resolveMutation = useMutation({
    mutationFn: () => reviewCommentApi.resolveComment(reportId, comment.id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['reviewComments', reportId] });
      toast.success('Comment resolved');
    },
    onError: (error: any) => {
      toast.error(error.response?.data?.error || 'Unable to resolve comment. Please try again.');
    },
  });

  const unresolveMutation = useMutation({
    mutationFn: () => reviewCommentApi.unresolveComment(reportId, comment.id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['reviewComments', reportId] });
      toast.success('Comment reopened');
    },
    onError: (error: any) => {
      toast.error(error.response?.data?.error || 'Unable to reopen comment. Please try again.');
    },
  });

  const authorName = comment.author.firstName
    ? `${comment.author.firstName} ${comment.author.lastName || ''}`.trim()
    : comment.author.email;

  return (
    <div className={`${isReply ? 'ml-8 border-l-2 border-gray-200 pl-4' : ''} ${comment.resolved ? 'opacity-60' : ''}`}>
      <div className="flex items-start gap-3">
        {/* Avatar */}
        <div className={`w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 text-xs font-bold ${
          comment.resolved ? 'bg-gray-200 text-gray-500' : 'bg-ocean-100 text-ocean-700'
        }`}>
          {(comment.author.firstName?.[0] || comment.author.email[0]).toUpperCase()}
        </div>

        <div className="flex-1 min-w-0">
          {/* Header */}
          <div className="flex items-center gap-2 flex-wrap">
            <span className="text-sm font-medium text-gray-900">{authorName}</span>
            <span className="text-xs text-gray-500">
              {formatDistanceToNow(new Date(comment.createdAt), { addSuffix: true })}
            </span>
            {comment.violationRef && (
              <span className="text-xs bg-red-50 text-red-700 px-1.5 py-0.5 rounded font-medium">
                {comment.violationRef}
              </span>
            )}
            {comment.sectionRef && (
              <span className="text-xs bg-blue-50 text-blue-700 px-1.5 py-0.5 rounded font-medium">
                {comment.sectionRef}
              </span>
            )}
            {comment.resolved && (
              <span className="inline-flex items-center gap-1 text-xs text-green-700 bg-green-50 px-1.5 py-0.5 rounded">
                <CheckCircleIcon className="h-3 w-3" />
                Resolved
              </span>
            )}
          </div>

          {/* Content */}
          <p className="text-sm text-gray-700 mt-1 whitespace-pre-wrap">{comment.content}</p>

          {/* Actions */}
          {!isReply && (
            <div className="flex items-center gap-3 mt-2">
              {onReply && (
                <button
                  onClick={() => onReply(comment.id)}
                  className="text-xs text-gray-500 hover:text-ocean-600 font-medium transition-colors"
                >
                  Reply
                </button>
              )}
              {!comment.resolved ? (
                <button
                  onClick={() => resolveMutation.mutate()}
                  disabled={resolveMutation.isPending}
                  className="text-xs text-gray-500 hover:text-green-600 font-medium transition-colors"
                >
                  {resolveMutation.isPending ? 'Resolving...' : 'Resolve'}
                </button>
              ) : (
                <button
                  onClick={() => unresolveMutation.mutate()}
                  disabled={unresolveMutation.isPending}
                  className="text-xs text-gray-500 hover:text-amber-600 font-medium transition-colors"
                >
                  {unresolveMutation.isPending ? 'Reopening...' : 'Reopen'}
                </button>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Replies */}
      {comment.replies && comment.replies.length > 0 && (
        <div className="mt-3 space-y-3">
          {comment.replies.map((reply) => (
            <CommentItem
              key={reply.id}
              comment={reply}
              reportId={reportId}
              isReply={true}
            />
          ))}
        </div>
      )}
    </div>
  );
}

export default function ReviewComments({ reportId }: ReviewCommentsProps) {
  const queryClient = useQueryClient();
  const [newComment, setNewComment] = useState('');
  const [replyToId, setReplyToId] = useState<string | null>(null);
  const [violationRef, setViolationRef] = useState('');
  const [sectionRef, setSectionRef] = useState('');
  const [showReferences, setShowReferences] = useState(false);
  const [showResolved, setShowResolved] = useState(false);

  const { data: commentsData, isLoading } = useQuery({
    queryKey: ['reviewComments', reportId],
    queryFn: async () => {
      const response = await reviewCommentApi.getComments(reportId);
      return response.data;
    },
  });

  const addCommentMutation = useMutation({
    mutationFn: async () => {
      return reviewCommentApi.addComment(reportId, {
        content: newComment,
        violationRef: violationRef || null,
        sectionRef: sectionRef || null,
        parentId: replyToId,
      });
    },
    onSuccess: () => {
      setNewComment('');
      setViolationRef('');
      setSectionRef('');
      setReplyToId(null);
      setShowReferences(false);
      queryClient.invalidateQueries({ queryKey: ['reviewComments', reportId] });
      toast.success('Comment added');
    },
    onError: (error: any) => {
      toast.error(error.response?.data?.error || 'Unable to add comment. Please try again.');
    },
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!newComment.trim()) return;
    addCommentMutation.mutate();
  };

  const handleReply = (parentId: string) => {
    setReplyToId(parentId);
    // Focus the input
    const input = document.getElementById('comment-input');
    if (input) input.focus();
  };

  const comments: Comment[] = commentsData?.comments || [];
  const visibleComments = showResolved
    ? comments
    : comments.filter((c) => !c.resolved);

  return (
    <div className="border border-gray-200 rounded-lg bg-white">
      {/* Header */}
      <div className="flex items-center justify-between px-4 py-3 border-b border-gray-200 bg-gray-50">
        <div className="flex items-center gap-2">
          <ChatBubbleLeftRightIcon className="h-5 w-5 text-ocean-600" />
          <span className="text-sm font-semibold text-navy-900">Review Comments</span>
          {commentsData && (
            <span className="text-xs text-gray-500">
              ({commentsData.unresolvedCount} unresolved, {commentsData.totalCount} total)
            </span>
          )}
        </div>

        {comments.some((c) => c.resolved) && (
          <button
            onClick={() => setShowResolved(!showResolved)}
            className="text-xs text-gray-500 hover:text-ocean-600 font-medium transition-colors"
          >
            {showResolved ? 'Hide resolved' : 'Show resolved'}
          </button>
        )}
      </div>

      {/* Comments list */}
      <div className="p-4 space-y-4 max-h-96 overflow-y-auto">
        {isLoading ? (
          <p className="text-sm text-gray-500">Loading comments...</p>
        ) : visibleComments.length === 0 ? (
          <p className="text-sm text-gray-400 text-center py-4">
            No comments yet. Add feedback below.
          </p>
        ) : (
          visibleComments.map((comment) => (
            <CommentItem
              key={comment.id}
              comment={comment}
              reportId={reportId}
              onReply={handleReply}
            />
          ))
        )}
      </div>

      {/* New comment form */}
      <div className="border-t border-gray-200 p-4">
        {replyToId && (
          <div className="flex items-center gap-2 mb-2 text-xs text-gray-500">
            <ArrowUturnLeftIcon className="h-3 w-3" />
            <span>Replying to comment</span>
            <button
              onClick={() => setReplyToId(null)}
              className="text-red-500 hover:text-red-700 font-medium"
            >
              Cancel
            </button>
          </div>
        )}

        <form onSubmit={handleSubmit} className="space-y-2">
          <textarea
            id="comment-input"
            value={newComment}
            onChange={(e) => setNewComment(e.target.value)}
            placeholder="Add a comment... (feedback, review notes, suggestions)"
            rows={2}
            className="block w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:border-ocean-500 focus:outline-none focus:ring-ocean-500 resize-none"
          />

          {/* Optional reference fields */}
          {showReferences && (
            <div className="grid grid-cols-2 gap-2">
              <input
                type="text"
                value={violationRef}
                onChange={(e) => setViolationRef(e.target.value)}
                placeholder="Rule reference (e.g., FDIC-AD-001)"
                className="block w-full rounded-md border border-gray-300 px-3 py-1.5 text-xs focus:border-ocean-500 focus:outline-none focus:ring-ocean-500"
              />
              <input
                type="text"
                value={sectionRef}
                onChange={(e) => setSectionRef(e.target.value)}
                placeholder="Section (e.g., FDIC Logo, APR)"
                className="block w-full rounded-md border border-gray-300 px-3 py-1.5 text-xs focus:border-ocean-500 focus:outline-none focus:ring-ocean-500"
              />
            </div>
          )}

          <div className="flex items-center justify-between">
            <button
              type="button"
              onClick={() => setShowReferences(!showReferences)}
              className="text-xs text-gray-500 hover:text-ocean-600 font-medium transition-colors"
            >
              {showReferences ? 'Hide references' : '+ Add rule/section reference'}
            </button>

            <button
              type="submit"
              disabled={!newComment.trim() || addCommentMutation.isPending}
              className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-ocean-600 text-white text-sm font-medium rounded-md hover:bg-ocean-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >
              <PaperAirplaneIcon className="h-4 w-4" />
              {addCommentMutation.isPending ? 'Posting...' : 'Post'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
